import { useEffect, useState, useRef } from 'react';
import { Handle, Position } from 'reactflow';

const extractVariables = (text) => {
  const regex = /{{\s*([a-zA-Z_$][\w$]*)\s*}}/g;
  const vars = new Set();
  let match;
  while ((match = regex.exec(text)) !== null) {
    vars.add(match[1]);
  }
  return Array.from(vars);
};

export const TextNode = ({ id, data }) => {
  const [currText, setCurrText] = useState(data?.text || '{{input}}');
  const textareaRef = useRef(null);

  const NODE_WIDTH = 260;

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [currText]);

  const variables = extractVariables(currText);

  const HEADER_HEIGHT = 38;
  const BODY_PADDING_TOP = 10;
  const LABEL_HEIGHT = 18;
  const TEXTAREA_MIN = 36;
  const PILLS_HEIGHT = variables.length > 0 ? 30 : 0;
  const BODY_PADDING_BOTTOM = 12;

  const contentHeight =
    HEADER_HEIGHT + BODY_PADDING_TOP + LABEL_HEIGHT + TEXTAREA_MIN + PILLS_HEIGHT + BODY_PADDING_BOTTOM;

  const handlesHeight =
    variables.length > 0
      ? HEADER_HEIGHT + BODY_PADDING_TOP + LABEL_HEIGHT + (variables.length - 1) * 28 + 28
      : 0;

  const minNodeHeight = Math.max(contentHeight, handlesHeight);

  // Output handle sits fixed at the top-right (header level)
  const OUTPUT_HANDLE_TOP = HEADER_HEIGHT / 2;

  return (
    <div
      style={{
        width: NODE_WIDTH,
        minHeight: minNodeHeight,
        boxSizing: 'border-box',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        background: '#ffffff',
        borderRadius: '12px',
        border: '1.5px solid #E5E7EB',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        position: 'relative',
      }}
    >
      {/* ── Header ── */}
      <div
        style={{
          background: '#FFF7ED',
          borderRadius: '10px 10px 0 0',
          padding: '8px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          borderBottom: '0.5px solid #E5E7EB',
        }}
      >
        <div
          style={{
            width: 22, height: 22,
            borderRadius: 6,
            background: '#FED7AA',
            color: '#C2410C',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 9,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          T
        </div>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
          Text
        </span>
      </div>

      {/* ── Body ── */}
      <div style={{ padding: '10px 12px 12px' }}>
        <span
          style={{
            display: 'block',
            fontSize: 10,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: '#9CA3AF',
            marginBottom: 4,
          }}
        >
          Content
        </span>

        <textarea
          ref={textareaRef}
          value={currText}
          onChange={(e) => setCurrText(e.target.value)}
          rows={1}
          placeholder="Enter text or {{ variable }}..."
          style={{
            width: '100%',
            padding: '6px 9px',
            fontSize: 12,
            fontFamily: 'monospace',
            lineHeight: 1.6,
            background: '#F9FAFB',
            border: '0.5px solid #E5E7EB',
            borderRadius: 6,
            resize: 'none',
            overflow: 'hidden',
            minHeight: 32,
            boxSizing: 'border-box',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            color: '#111827',
            outline: 'none',
          }}
          onFocus={(e) => {
            e.target.style.borderColor = '#3B82F6';
            e.target.style.boxShadow = '0 0 0 2px rgba(59,130,246,0.15)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#E5E7EB';
            e.target.style.boxShadow = 'none';
          }}
        />

        {/* Variable pills */}
        {variables.length > 0 && (
          <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {variables.map((variable) => (
              <span
                key={variable}
                style={{
                  display: 'inline-block',
                  background: '#F0FDF4',       // green tint — matches handle color
                  border: '0.5px solid #86EFAC',
                  borderRadius: 4,
                  padding: '2px 7px',
                  fontSize: 10,
                  fontFamily: 'monospace',
                  color: '#16A34A',             // green text
                  fontWeight: 500,
                }}
              >
                {`{{${variable}}}`}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ── Output handle (right side, fixed at header level, orange) ── */}
      {/* Orange = output, matches the Text node theme color */}
      <Handle
        type="source"
        position={Position.Right}
        id={`${id}-output`}
        style={{
          width: 11,
          height: 11,
          background: '#F97316',          // orange = output
          border: '2px solid white',
          borderRadius: '50%',
          boxShadow: '0 0 0 1px rgba(0,0,0,0.1)',
          top: OUTPUT_HANDLE_TOP,         // pinned to header, never moves
          right: -6,
          transform: 'none',
        }}
      />

      {/* ── Input handles (left side, green, one per variable) ── */}
      {/* Green = input, visually distinct from orange output */}
      {variables.map((variable, index) => (
        <Handle
          key={variable}
          type="target"
          position={Position.Left}
          id={`${id}-${variable}`}
          style={{
            width: 11,
            height: 11,
            background: '#22C55E',        // green = input
            border: '2px solid white',
            borderRadius: '50%',
            boxShadow: '0 0 0 1px rgba(0,0,0,0.1)',
            top: HEADER_HEIGHT + BODY_PADDING_TOP + LABEL_HEIGHT + index * 28,
            left: -6,
            transform: 'none',
          }}
        />
      ))}
    </div>
  );
};